/**
 * Version beacon (#237) — helpers puros da detecção de build antigo.
 *
 * O problema: o chat é rota única e uma aba aberta antes do deploy segue
 * rodando o código velho por DIAS (socket funcionando, zero erros),
 * reproduzindo bugs que a versão nova já corrigiu. O service worker agrava:
 * cada push foca a aba existente (velha) em vez de abrir uma nova.
 *
 * A detecção compara a versão baked no build (NEXT_PUBLIC_APP_VERSION) com a
 * que a API anuncia (socket `server:version` no connect + `/health` no
 * visibilitychange). A integração com toast/reload fica em
 * `use-version-beacon.ts`; aqui só a lógica testável.
 */

type StorageLike = Pick<Storage, "getItem" | "setItem">;

/** Chave do guard anti-loop no sessionStorage (por aba, morre com ela). */
export const RELOADED_KEY = "gk-version-reloaded-for";

/** Versão deste build, baked pelo next.config. null = dev/env ausente. */
export const LOCAL_VERSION: string | null =
  process.env.NEXT_PUBLIC_APP_VERSION || null;

/**
 * Mismatch real exige as DUAS versões conhecidas: local vazio (dev, build
 * antigo sem a env) ou servidor vazio/'unknown' (API sem package.json
 * resolvido) viram no-op — o beacon nunca pode disparar reload por falha de
 * configuração, só por diferença comprovada.
 */
export function isVersionMismatch(
  local: string | null | undefined,
  server: string | null | undefined,
): boolean {
  if (!local || !server || server === "unknown") return false;
  return local !== server;
}

/**
 * Guard anti-loop: já recarregamos esta aba por causa DESTA versão do
 * servidor? Se o reload não resolveu o mismatch (ex.: deploy pela metade,
 * web antigo ainda no ar), não ficar em loop infinito de reload — mostra só
 * o toast. Storage inacessível (Safari private mode) responde `true`
 * (= não recarregar): sem guard confiável, loop é o risco pior.
 */
export function hasReloadedFor(
  storage: StorageLike,
  serverVersion: string,
): boolean {
  try {
    return storage.getItem(RELOADED_KEY) === serverVersion;
  } catch {
    return true;
  }
}

export function markReloadedFor(
  storage: StorageLike,
  serverVersion: string,
): void {
  try {
    storage.setItem(RELOADED_KEY, serverVersion);
  } catch {
    // sem storage o guard hasReloadedFor já bloqueia o auto-reload
  }
}
